(function () {
  'use strict';

  const PINCODE_PATTERN = /^\d{6}$/;

  function parseExcludedPincodes(value) {
    if (!value) return new Set();
    return new Set(
      value
        .split(',')
        .map((pin) => pin.trim())
        .filter((pin) => PINCODE_PATTERN.test(pin))
    );
  }

  function formatMessage(template, replacements) {
    return Object.entries(replacements).reduce(
      (message, [key, value]) => message.split(`[[${key}]]`).join(value),
      template
    );
  }

  function getLocationLabel(postOffices) {
    if (!Array.isArray(postOffices) || !postOffices.length) return '';
    const office = postOffices[0];
    const parts = [office.District, office.State].filter(Boolean);
    return parts.join(', ');
  }

  function setResult(resultEl, state, message) {
    if (!resultEl) return;
    resultEl.hidden = false;
    resultEl.textContent = message;
    resultEl.classList.remove(
      'product-delivery-check__result--success',
      'product-delivery-check__result--error',
      'product-delivery-check__result--warning'
    );
    resultEl.classList.add(`product-delivery-check__result--${state}`);
  }

  function clearResult(resultEl) {
    if (!resultEl) return;
    resultEl.hidden = true;
    resultEl.textContent = '';
    resultEl.classList.remove(
      'product-delivery-check__result--success',
      'product-delivery-check__result--error',
      'product-delivery-check__result--warning'
    );
  }

  async function fetchPincodeData(apiUrl, pincode) {
    const response = await fetch(`${apiUrl}${encodeURIComponent(pincode)}`, {
      method: 'GET',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const data = await response.json();
    const payload = Array.isArray(data) ? data[0] : data;

    if (!payload || payload.Status !== 'Success' || !Array.isArray(payload.PostOffice) || !payload.PostOffice.length) {
      return null;
    }

    return payload.PostOffice;
  }

  async function handleDeliveryCheck(widget) {
    const input = widget.querySelector('[data-delivery-pincode]');
    const button = widget.querySelector('[data-delivery-check-btn]');
    const resultEl = widget.querySelector('[data-delivery-result]');

    if (!input || !button || !resultEl) return;

    const pincode = input.value.trim();
    const excludedPincodes = parseExcludedPincodes(widget.dataset.excludedPincodes || '');
    const deliveryTime = widget.dataset.deliveryTime || '6-7 days';

    if (!PINCODE_PATTERN.test(pincode)) {
      setResult(resultEl, 'error', widget.dataset.msgInvalid || 'Please enter a valid 6-digit pincode.');
      return;
    }

    const originalLabel = button.textContent;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    clearResult(resultEl);

    try {
      const postOffices = await fetchPincodeData(widget.dataset.apiUrl || 'https://api.postalpincode.in/pincode/', pincode);

      if (!postOffices) {
        setResult(resultEl, 'error', widget.dataset.msgInvalid || 'Please enter a valid 6-digit pincode.');
        return;
      }

      if (excludedPincodes.has(pincode)) {
        setResult(
          resultEl,
          'warning',
          widget.dataset.msgUnavailable || 'Delivery not available at this location.'
        );
        return;
      }

      const location = getLocationLabel(postOffices);
      const message = formatMessage(widget.dataset.msgAvailable || 'Delivery in [[time]] to [[location]].', {
        time: deliveryTime,
        location: location || pincode
      });

      setResult(resultEl, 'success', message);
    } catch (error) {
      setResult(resultEl, 'error', widget.dataset.msgError || 'Unable to check delivery. Please try again.');
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.textContent = originalLabel;
    }
  }

  function bindDeliveryCheck(widget) {
    if (!widget || widget.dataset.deliveryCheckInit === 'true') return;
    widget.dataset.deliveryCheckInit = 'true';

    const input = widget.querySelector('[data-delivery-pincode]');
    const button = widget.querySelector('[data-delivery-check-btn]');

    button?.addEventListener('click', () => {
      handleDeliveryCheck(widget);
    });

    input?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleDeliveryCheck(widget);
      }
    });

    input?.addEventListener('input', () => {
      input.value = input.value.replace(/\D/g, '').slice(0, 6);
      if (!input.value) {
        clearResult(widget.querySelector('[data-delivery-result]'));
      }
    });
  }

  function initProductDeliveryCheck(root = document) {
    const scope = root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-delivery-check]').forEach(bindDeliveryCheck);
  }

  window.initProductDeliveryCheck = initProductDeliveryCheck;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initProductDeliveryCheck());
  } else {
    initProductDeliveryCheck();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initProductDeliveryCheck(event.target);
  });
})();
